import * as FileSystem from 'expo-file-system';
import * as ImageManipulator from 'expo-image-manipulator';
import { Platform } from 'react-native';
import { SHELF_LIFE_PROMPT_BLOCK } from './shelfLifePrompt';

export interface ProcessedImage {
  base64: string;
  mimeType: string;
  originalSize?: number;
  compressedSize?: number;
  compressionRatio?: number;
}

export interface ImageCompressionConfig {
  enabled: boolean;
  maxWidth: number;
  maxHeight: number;
  quality: number;
  format: ImageManipulator.SaveFormat;
  /**
   * When true, resize passes only `width` so ImageManipulator preserves the
   * aspect ratio. Passing both width and height forces those exact dimensions
   * and distorts any photo that isn't already 3:4 — fatal for receipt text.
   */
  preserveAspectRatio?: boolean;
}

// Default compression configuration based on our test results
const DEFAULT_COMPRESSION_CONFIG: ImageCompressionConfig = {
  enabled: true,
  maxWidth: 800,
  maxHeight: 1067,
  quality: 0.7,
  format: ImageManipulator.SaveFormat.JPEG,
  preserveAspectRatio: false
};

/**
 * Converts an image URI to base64 format for AI processing
 * Compresses and returns base64 in a single step for best performance
 */
export const convertImageToBase64 = async (
  imageUri: string,
  compressionConfig?: Partial<ImageCompressionConfig>
): Promise<ProcessedImage> => {
  try {
    const config: ImageCompressionConfig = {
      ...DEFAULT_COMPRESSION_CONFIG,
      ...compressionConfig
    };

    if (!config.enabled) {
      // No compression - read directly
      let base64: string;
      if (Platform.OS === 'web') {
        const response = await fetch(imageUri);
        const blob = await response.blob();
        base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = reader.result as string;
            const base64Data = result.split(',')[1] || result;
            resolve(base64Data);
          };
          reader.onerror = () => reject(reader.error || new Error('Failed to read image file'));
          reader.readAsDataURL(blob);
        });
      } else {
        base64 = await FileSystem.readAsStringAsync(imageUri, {
          encoding: FileSystem.EncodingType.Base64,
        });
      }
      return { base64, mimeType: 'image/jpeg' };
    }

    // Width-only resize preserves aspect ratio (receipts); width+height forces
    // exact dimensions (legacy fridge behavior).
    const resizeAction = config.preserveAspectRatio
      ? { resize: { width: config.maxWidth } }
      : { resize: { width: config.maxWidth, height: config.maxHeight } };

    if (Platform.OS === 'web') {
      // Web: compress then fetch as blob
      const compressedImage = await ImageManipulator.manipulateAsync(
        imageUri,
        [resizeAction],
        { compress: config.quality, format: config.format, base64: false }
      );
      const response = await fetch(compressedImage.uri);
      const blob = await response.blob();
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          const base64Data = result.split(',')[1] || result;
          resolve(base64Data);
        };
        reader.onerror = () => reject(reader.error || new Error('Failed to read image file'));
        reader.readAsDataURL(blob);
      });
      return { base64, mimeType: 'image/jpeg' };
    }

    // Mobile: compress with base64: true to avoid separate file read
    const compressedImage = await ImageManipulator.manipulateAsync(
      imageUri,
      [resizeAction],
      { compress: config.quality, format: config.format, base64: true }
    );

    if (!compressedImage.base64) {
      // Fallback: read from disk if base64 wasn't returned
      const base64 = await FileSystem.readAsStringAsync(compressedImage.uri, {
        encoding: FileSystem.EncodingType.Base64,
      });
      return { base64, mimeType: 'image/jpeg' };
    }

    return { base64: compressedImage.base64, mimeType: 'image/jpeg' };
  } catch (error) {
    console.error('ImageProcessor - Error processing image:', error);
    throw new Error(`Failed to process image: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/** Read the pixel dimensions of an image without re-encoding it. */
export const getImageDimensions = async (
  imageUri: string,
): Promise<{ width: number; height: number }> => {
  // A no-op manipulate is the one API that reports dimensions on both web and native.
  const info = await ImageManipulator.manipulateAsync(imageUri, [], { base64: false });
  return { width: info.width, height: info.height };
};

/**
 * Scale an image to `targetWidth` once, up front, so the bands can be cropped
 * out of the (much smaller) result.
 *
 * The tiled receipt scan used to crop each band straight from the original and
 * resize it, which decoded the full-size photo once per band — five full
 * decodes of a 12 MP image for a four-band scan. That is expensive on the main
 * thread and gets far worse in a backgrounded browser tab, where the work is
 * throttled. Resizing first is output-equivalent (crop-then-scale and
 * scale-then-crop resample to the same band) at a fraction of the cost.
 */
export const prepareImageForBanding = async (
  imageUri: string,
  targetWidth: number,
  quality: number,
): Promise<{ uri: string; width: number; height: number }> => {
  const result = await ImageManipulator.manipulateAsync(
    imageUri,
    [{ resize: { width: targetWidth } }],
    { compress: quality, format: ImageManipulator.SaveFormat.JPEG, base64: false },
  );
  return { uri: result.uri, width: result.width, height: result.height };
};

/**
 * Crop one horizontal band out of an already-prepared image and return it as
 * base64. The source is expected to be the output of prepareImageForBanding, so
 * no rescaling happens here. Used by the tiled receipt pipeline
 * (see src/utils/receiptTiling.ts).
 */
export const extractImageBand = async (
  imageUri: string,
  band: { top: number; height: number },
  sourceWidth: number,
  quality: number,
): Promise<ProcessedImage> => {
  const result = await ImageManipulator.manipulateAsync(
    imageUri,
    [{ crop: { originX: 0, originY: band.top, width: sourceWidth, height: band.height } }],
    {
      compress: quality,
      format: ImageManipulator.SaveFormat.JPEG,
      base64: Platform.OS !== 'web',
    },
  );

  if (result.base64) {
    return { base64: result.base64, mimeType: 'image/jpeg' };
  }

  // Web (and the native fallback) read the produced file back out.
  if (Platform.OS === 'web') {
    const response = await fetch(result.uri);
    const blob = await response.blob();
    const base64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const value = reader.result as string;
        resolve(value.split(',')[1] || value);
      };
      reader.onerror = () => reject(reader.error || new Error('Failed to read image band'));
      reader.readAsDataURL(blob);
    });
    return { base64, mimeType: 'image/jpeg' };
  }

  const base64 = await FileSystem.readAsStringAsync(result.uri, {
    encoding: FileSystem.EncodingType.Base64,
  });
  return { base64, mimeType: 'image/jpeg' };
};

/**
 * Gets compression configuration from environment variables
 */
export const getCompressionConfigFromEnv = (): Partial<ImageCompressionConfig> => {
  const envConfig: Partial<ImageCompressionConfig> = {};

  // Check for environment variable overrides
  if (process.env.EXPO_PUBLIC_IMAGE_COMPRESSION_ENABLED !== undefined) {
    envConfig.enabled = process.env.EXPO_PUBLIC_IMAGE_COMPRESSION_ENABLED === 'true';
  }

  if (process.env.EXPO_PUBLIC_IMAGE_MAX_WIDTH) {
    envConfig.maxWidth = parseInt(process.env.EXPO_PUBLIC_IMAGE_MAX_WIDTH, 10);
  }

  if (process.env.EXPO_PUBLIC_IMAGE_MAX_HEIGHT) {
    envConfig.maxHeight = parseInt(process.env.EXPO_PUBLIC_IMAGE_MAX_HEIGHT, 10);
  }

  if (process.env.EXPO_PUBLIC_IMAGE_QUALITY) {
    envConfig.quality = parseFloat(process.env.EXPO_PUBLIC_IMAGE_QUALITY);
  }

  return envConfig;
};

/**
 * Gets the specialized food recognition prompt for Gemini
 * @param language - The language to use for ingredient names ('en' or 'fr')
 */
export const getFoodRecognitionPrompt = (language: 'en' | 'fr' = 'en'): string => {
  const languageInstructions = language === 'fr'
    ? `IMPORTANT - LANGUAGE: Return ALL food item names in FRENCH (français).
   • "Milk" → "Lait" | "Cheese" → "Fromage" | "Eggs" → "Oeufs"
   • "Apples" → "Pommes" | "Tomatoes" → "Tomates" | "Lettuce" → "Laitue"
   • "Chicken" → "Poulet" | "Beef" → "Boeuf" | "Fish" → "Poisson"
   • Use proper French names for ALL food items and brands when applicable`
    : `IMPORTANT - LANGUAGE: Return ALL food item names in ENGLISH.
   • Keep all item names in English`;

  return `You are a specialized food recognition AI assistant designed to analyze refrigerator photos and create an accurate, detailed food inventory. Your task is to identify all food items visible in the provided image with high precision and return the results ONLY in the specified JSON format.

${languageInstructions}

Analysis Guidelines:
1. Comprehensive Scan: Look carefully at all shelves, door compartments, drawers, and containers.
2. Item Specificity: Be as specific as possible. Identify the contents, not just the container (e.g., "Leftover Pasta" instead of "Tupperware").
3. Brand and Label Recognition: Pay close attention to brand names and product labels. Include brand names in the item description when clearly readable (e.g., "Quebon 2% Milk", Pico pico sauce)
4. Category Guidance:
   • Use "Plant-Based Protein" for items like chickpeas, lentils, beans, tofu, tempeh, edamame, and other legumes
   • Use "Meat" for animal-based proteins (chicken, beef, fish, etc.)
   • Use "Dairy" for milk products (milk, cheese, yogurt, eggs, butter)
5. Packaged & Obscured Items:
   • For items in transparent bags or containers, identify the visible contents (e.g., "Spinach in plastic bag").
   • For opaque or partially obscured items, use contextual clues like shape, size, and location to make an educated guess, and reflect this in the confidence score.
6. Confidence Score: Use confidence scores thoughtfully to reflect your certainty:
   • 90-100%: Completely certain identification with clear visibility.
   • 70-89%: Very confident; the item is mostly visible but may have minor obstructions.
   • 50-69%: Reasonably sure; based on partial views or recognizable packaging shapes.
   • 30-49%: Educated guess based on limited visual clues.
   • Below 30%: Do not include the item.
7. Quantity Estimation:
   • Count visible individual items (e.g., "3" apples, "2" bottles)
   • Estimate from packaging when visible (e.g., "12" for egg carton)
   • Use "unknown" for bulk items (flour, spices, oils)
   • When uncertain, use conservative count
   • IMPORTANT — Label Reading: When a product has a visible weight, volume, or quantity label on its packaging (e.g., "500g", "1L", "250ml", "6 pack"), read and use that exact measurement. Return the numeric value in the "quantity" field and the unit in the "unit" field (e.g., quantity: 500, unit: "g"). This takes priority over generic estimates.
8. ${SHELF_LIFE_PROMPT_BLOCK}

Response Format:
Respond ONLY with valid JSON in this exact format. Do not include any explanatory text, markdown formatting, or additional commentary.

Note: shelf_life_estimate is REQUIRED for all identified food items. The position field is REQUIRED for every detected item.

{
  "detected_items": [
    {
      "name": "string",
      "category": "Fruits | Vegetables | Dairy | Meat | Plant-Based Protein | Beverages | Condiments | Spices | Leftovers | Packaged Foods | Bread/Grains | Other",
      "confidence": "number (0-100)",
      "quantity_estimate": "number (1, 2, 3...) or 'unknown' if cannot determine",
      "quantity": "number (optional — exact quantity read from packaging label, e.g. 500)",
      "unit": "string (optional — unit read from packaging label, e.g. 'g', 'ml', 'L')",
      "visible_condition": "fresh | good | wilting | near-expiry | unknown",
      "shelf_life_estimate": "highly-perishable | perishable | week-plus | keeps-weeks | shelf-stable",
      "opened": "boolean (true when the package is open or the item is cut)",
      "position": {
        "x": "number (percentage from left, 0-100)",
        "y": "number (percentage from top, 0-100)",
        "width": "number (percentage width, 0-100)",
        "height": "number (percentage height, 0-100)"
      }
    }
  ],
  "analysis_notes": {
    "image_quality": "Poor | Fair | Good | Excellent",
    "lighting_conditions": "Poor | Fair | Good | Excellent",
    "total_items_detected": "number",
    "areas_analyzed": ["Main shelves", "Door compartments", "Crisper drawers", "Freezer section"]
  }
}

Position Guidelines (CRITICAL - position is REQUIRED for every item):
- You MUST provide position data for EVERY detected item. Items without position data cannot be displayed to the user.
- Provide approximate bounding box coordinates as percentages (0-100) relative to image dimensions
- x,y represent the top-left corner of the bounding box
- width,height represent the size of the bounding box
- Estimate the bounding box even if the item is partially obscured`;
};